// ============================================
// B2：3D 视口内组件就地编辑层（独立编辑窗口）
// - 五模式：select（框选）/ move / rotate / resize(box/e/w/s/n) / binding
// - 悬浮工具条：模式切换 + 图层/属性/保存 + Alt 交还相机
// - 右侧就地属性浮窗：复用 ConfigPanel（宽 300）
// - 历史记录：操作前 push，Ctrl+Z/Y 撤销重做；Alt = 交还 3D 相机
// ============================================
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Space, Tooltip } from 'antd';
import {
  AimOutlined, ApartmentOutlined, ArrowsAltOutlined, CloseOutlined,
  DragOutlined, ExpandOutlined, RedoOutlined, RetweetOutlined,
  SaveOutlined, UndoOutlined, ZoomInOutlined,
} from '@ant-design/icons';
import { useDrop } from 'react-dnd';
import type { VisualComponent } from '@dtwin/shared';
import useDashboardStore from '../../stores/dashboardStore';
import useEditorUiStore from '../../stores/editorUiStore';
import { useDashboardHistory, beginGestureHistory, endGestureHistory } from '../../stores/dashboardHistory';
import { useSceneSave } from '../../hooks/useSceneSave';
import CompPreviewBox from './CompPreviewBox';
import { applyResize, clampPosition, intersects, screenToPageDelta } from '../../utils/overlayEdit';
import { dropPagePosition } from '../../utils/dashLayerTree';

type Mode = 'select' | 'move' | 'rotate' | 'resize' | 'binding';

const ROTATE_SNAP = [
  { d: 0, on: true },
  { d: 15, on: false },
  { d: 30, on: true },
  { d: 45, on: true },
  { d: 60, on: false },
  { d: 90, on: true },
  { d: 135, on: true },
  { d: 180, on: true },
];

/** union 工具条按钮组（当前模式高亮） */
function ToolButton({
  active, title, icon, onClick,
}: { active?: boolean; title: string; icon: React.ReactNode; onClick: () => void }) {
  return (
    <Tooltip title={title}>
      <Button
        size="small"
        type={active ? 'primary' : 'text'}
        icon={icon}
        onClick={onClick}
        data-testid={`ov-tool-${title}`}
      />
    </Tooltip>
  );
}

export default function DashboardViewportEditLayer({ sceneId }: { sceneId: string }) {
  const components = useDashboardStore((s) => s.components);
  const currentPage = useDashboardStore((s) => s.currentPage);
  const dirty = useDashboardStore((s) => s.dirty);
  const saving = useDashboardStore((s) => s.saving);
  const saveError = useDashboardStore((s) => s.saveError);
  const selectedComponentId = useDashboardStore((s) => s.selectedComponentId);
  const selectedComponentIds = useDashboardStore((s) => s.selectedComponentIds);
  const { selectComponent, selectMultiple, reorderComponent, resizeComponent } = useDashboardStore.getState();
  const { push: pushHistory, undo, redo } = useDashboardHistory();
  const altCamera = useEditorUiStore((s) => s.overlayAltCamera);
  const setAltCamera = useEditorUiStore((s) => s.setOverlayAltCamera);

  const [mode, setMode] = useState<Mode>('select');
  // 2026-10-05：属性/绑定在右侧 3D 属性区（Tabs：属性配置/图层）；组件库在左侧图层详情的红框区（LayerPanel）
  const { save: saveSceneAndDash, saving: sceneSaving } = useSceneSave();
  // G4（2026-10-04）：2D 组件右键菜单（复制/删除/置顶/置底/锁定/属性/绑定）
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; compId: string } | null>(null);
  const [resizeHandle, setResizeHandle] = useState<'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'>('se');

  const shellRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState({ x: 1, y: 1 });
  const [marquee, setMarquee] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const gesture = useRef<any>(null);

  const anchorScale = currentPage?.sceneAnchor?.scale || 1;
  const pageW = currentPage?.config?.width || 1920;
  const pageH = currentPage?.config?.height || 1080;

  // 2026-10-04｜B-10：组件库拖拽投放（react-dnd）——落点换算为页面坐标、自动选中新组件
  const [, dropRef] = useDrop<{ type: string; size?: { width: number; height: number } }>(
    () => ({
      accept: 'VISUAL_COMPONENT',
      drop: (item, monitor) => {
        const offset = monitor.getClientOffset();
        const rect = pageRef.current?.getBoundingClientRect();
        if (!offset || !rect || !rect.width || !rect.height) return;
        const size = item.size || { width: 200, height: 100 };
        const pos = dropPagePosition(
          offset.x, offset.y, rect,
          { x: rect.width / pageW, y: rect.height / pageH }, 1,
          size, { width: pageW, height: pageH },
        );
        const before = useDashboardStore.getState().components.map((c) => c.id);
        pushHistory();
        useDashboardStore.getState().addComponent({
          type: item.type as VisualComponent['type'], position: pos, size, style: {}, config: {},
        } as any);
        const created = useDashboardStore.getState().components.find((c) => !before.includes(c.id));
        if (created) useDashboardStore.getState().selectComponent(created.id);
        useEditorUiStore.getState().closeWidgetPicker();
      },
    }),
    [pageW, pageH, pushHistory],
  );
  const attachShell = (el: HTMLDivElement | null) => {
    shellRef.current = el;
    dropRef(el);
  };

  // G4：Esc 关闭右键菜单
  useEffect(() => {
    if (!ctxMenu) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setCtxMenu(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ctxMenu]);

  // 手势批次兜底：组件卸载/退出就地编辑时恢复自动历史
  useEffect(() => () => { endGestureHistory(); }, []);

  const openCtxMenu = (e: React.MouseEvent, comp: VisualComponent) => {
    e.preventDefault();
    e.stopPropagation();
    useDashboardStore.getState().selectComponent(comp.id);
    const r = shellRef.current?.getBoundingClientRect();
    const x = e.clientX - (r?.left || 0);
    const y = e.clientY - (r?.top || 0);
    setCtxMenu({
      x: Math.min(x, Math.max(0, (r?.width || 0) - 150)),
      y: Math.min(y, Math.max(0, (r?.height || 0) - 270)),
      compId: comp.id,
    });
  };

  const ctxComp = ctxMenu ? components.find((c) => c.id === ctxMenu.compId) || null : null;
  const ctxLocked = !!(ctxComp as any)?.base?.locked;

  // 与只读预览一致的适配缩放（分轴拉伸）
  useEffect(() => {
    const el = shellRef.current;
    if (!el) return;
    const fit = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width <= 0 || height <= 0) return;
      const next = { x: width / pageW, y: height / pageH };
      setScale((prev) => (Math.abs(prev.x - next.x) < 1e-4 && Math.abs(prev.y - next.y) < 1e-4 ? prev : next));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    window.addEventListener('resize', fit);
    return () => { ro.disconnect(); window.removeEventListener('resize', fit); };
  }, [pageW, pageH]);

  // Alt 交还相机；Ctrl+Z/Y 撤销重做
  const clipboard = useRef<VisualComponent[]>([]);

  useEffect(() => {
    const isTyping = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    };
    const down = (e: KeyboardEvent) => {
      if (e.key === 'Alt') { setAltCamera(true); return; }
      if (isTyping(e.target)) return;
      const mod = e.ctrlKey || e.metaKey;
      const k = e.key.toLowerCase();
      if (mod && k === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
      if (mod && k === 'y') { e.preventDefault(); redo(); return; }
      if (mod && k === 'a') {
        e.preventDefault();
        const ids = useDashboardStore.getState().components.map((c) => c.id);
        if (ids.length) {
          selectComponent(ids[0]);
          ids.slice(1).forEach((id) => useDashboardStore.getState().selectMultiple(id));
        }
        return;
      }
      if (mod && k === 'c') {
        const { components: all, selectedComponentIds: sel } = useDashboardStore.getState();
        clipboard.current = all.filter((c) => sel.includes(c.id)).map((c) => JSON.parse(JSON.stringify(c)));
        return;
      }
      if (mod && k === 'v') {
        if (!clipboard.current.length) return;
        e.preventDefault();
        pushHistory();
        clipboard.current.forEach((c) => {
          const pos = clampPosition(c.position.x + 20, c.position.y + 20, c.size, { width: pageW, height: pageH });
          useDashboardStore.getState().addComponent({
            type: c.type,
            position: pos,
            size: { ...c.size },
            base: JSON.parse(JSON.stringify((c as any).base || {})),
            config: JSON.parse(JSON.stringify((c as any).config || {})),
            style: JSON.parse(JSON.stringify((c as any).style || {})),
          } as any);
        });
        return;
      }
      if (e.key === 'Escape') { selectComponent(null); return; }
      // 方向键微调（Shift=10px）
      const step = e.shiftKey ? 10 : 1;
      const dirs: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step],
      };
      const d = dirs[e.key];
      if (d && selectedComponentId) {
        e.preventDefault();
        const all = useDashboardStore.getState().components;
        const ids = useDashboardStore.getState().selectedComponentIds.length
          ? useDashboardStore.getState().selectedComponentIds
          : [selectedComponentId];
        ids.forEach((sid) => {
          const comp = all.find((c) => c.id === sid) as any;
          if (comp && !comp.base?.locked) {
            const pos = clampPosition(comp.position.x + d[0], comp.position.y + d[1], comp.size, { width: pageW, height: pageH });
            useDashboardStore.getState().reorderComponent(sid, pos);
          }
        });
      }
    };
    const up = (e: KeyboardEvent) => { if (e.key === 'Alt') setAltCamera(false); };
    const blur = () => setAltCamera(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, [selectComponent, undo, redo, setAltCamera, pushHistory, selectedComponentId, pageW, pageH]);

  const selected = useMemo(
    () => components.find((c) => c.id === selectedComponentId) || null,
    [components, selectedComponentId],
  );

  const pageRect = () => pageRef.current!.getBoundingClientRect();
  const scaleRef = { scaleX: scale.x, scaleY: scale.y, anchorScale };
  const toPage = (clientX: number, clientY: number) => {
    const r = pageRect();
    return { x: (clientX - r.left) / (scale.x * anchorScale), y: (clientY - r.top) / (scale.y * anchorScale) };
  };

  // ── 手势：移动/缩放/旋转/框选统一在编辑层（window）内跟踪 ──
  const startGesture = (
    e: React.PointerEvent,
    kind: 'move' | 'resize' | 'rotate',
    comp: VisualComponent,
    opts?: { selectTap?: boolean },
  ) => {
    e.stopPropagation();
    e.preventDefault();
    if (e.button !== 0) return;
    if ((comp as any).base?.locked) return;
    // select 态按住拖动：阈值判定（点按=选中，不产生历史；移动后才入历史）
    const needThreshold = kind === 'move' && !!opts?.selectTap;
    const freshIds = useDashboardStore.getState().selectedComponentIds;
    if (!freshIds.includes(comp.id)) selectComponent(comp.id);
    // 手势级历史批次：整段拖拽/缩放/旋转 = 1 个撤销步（阈值拖拽延迟到首次移动时开启）
    if (!needThreshold) beginGestureHistory();
    // 多选整体移动：记录所有已选组件的起始位置（排除锁定）
    const all = useDashboardStore.getState().components;
    const moveIds = kind === 'move'
      ? (freshIds.includes(comp.id) ? freshIds : [comp.id])
      : [comp.id];
    const starts = moveIds
      .map((sid) => all.find((c) => c.id === sid))
      .filter((c): c is VisualComponent => !!c && !(c as any).base?.locked)
      .map((c) => ({ id: c.id, pos: { ...c.position }, size: { ...c.size } }));
    if (starts.length === 0) starts.push({ id: comp.id, pos: { ...comp.position }, size: { ...comp.size } });
    gesture.current = {
      kind,
      id: comp.id,
      starts,
      startPos: { ...comp.position },
      startSize: { ...comp.size },
      startRot: Number((comp as any).base?.rotation ?? (comp as any).config?.rotation ?? 0) || 0,
      originX: e.clientX,
      originY: e.clientY,
      moved: false,
      threshold: needThreshold ? 3 : 0,
    };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const g = gesture.current;
    if (!g) return;
    const dx = e.clientX - g.originX;
    const dy = e.clientY - g.originY;
    if (g.kind === 'move') {
      // select 态点按拖动：超过阈值才算拖动（先入历史一次）
      if (g.threshold) {
        if (!g.moved) {
          if (Math.abs(dx) + Math.abs(dy) < g.threshold) return;
          // 阈值拖拽：首次真正移动才开启手势历史批次
          g.moved = true;
          beginGestureHistory();
        }
      }
      const d = screenToPageDelta(dx, dy, scaleRef);
      (g.starts as { id: string; pos: { x: number; y: number }; size: { width: number; height: number } }[]).forEach((s) => {
        const pos = clampPosition(s.pos.x + d.x, s.pos.y + d.y, s.size, { width: pageW, height: pageH });
        reorderComponent(s.id, pos);
      });
    } else if (g.kind === 'resize') {
      const d = screenToPageDelta(dx, dy, scaleRef);
      const next = applyResize(resizeHandle, { ...g.startPos, ...g.startSize }, d, { width: pageW, height: pageH });
      reorderComponent(g.id, { x: next.x, y: next.y });
      resizeComponent(g.id, { width: next.width, height: next.height });
    } else if (g.kind === 'rotate') {
      const r = pageRect();
      const cx = r.left + (g.startPos.x + g.startSize.width / 2) * scale.x * anchorScale;
      const cy = r.top + (g.startPos.y + g.startSize.height / 2) * scale.y * anchorScale;
      const ang = Math.round((Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI + 90);
      const normalized = ((ang % 360) + 360) % 360;
      const snap = ROTATE_SNAP.find((s) => Math.abs(s.d - normalized) <= 3);
      const rot = snap?.on ? snap.d : normalized;
      useDashboardStore.getState().updateComponent(g.id, {
        base: { ...(compBase(g.id) || {}), rotation: rot },
      } as any);
    }
  };

  const compBase = (id: string) => {
    const c = useDashboardStore.getState().components.find((x) => x.id === id) as any;
    return c?.base;
  };

  const endGesture = () => {
    if (gesture.current) endGestureHistory();
    gesture.current = null;
  };

  // 框选（允许从编辑层空白或页面空白（pageRef）起框）
  const onShellPointerDown = (e: React.PointerEvent) => {
    if (mode !== 'select' || e.button !== 0) return;
    const t = e.target as HTMLElement;
    if (t !== shellRef.current && t !== pageRef.current && !t.dataset?.marquee) return;
    const p = toPage(e.clientX, e.clientY);
    setMarquee({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
    gesture.current = { kind: 'marquee', originX: e.clientX, originY: e.clientY, start: p };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const onShellPointerMove = (e: React.PointerEvent) => {
    const g = gesture.current;
    if (!g || g.kind !== 'marquee') return;
    const p = toPage(e.clientX, e.clientY);
    setMarquee({ x1: g.start.x, y1: g.start.y, x2: p.x, y2: p.y });
  };

  const onShellPointerUp = () => {
    const g = gesture.current;
    if (g?.kind === 'marquee' && marquee) {
      const ids = components
        .filter((c) => !(c as any).base?.locked)
        .filter((c) => intersects(marquee, { ...c.position, ...c.size }))
        .map((c) => c.id);
      if (ids.length === 0) selectComponent(null);
      else {
        selectComponent(ids[0]);
        ids.slice(1).forEach((id) => useDashboardStore.getState().selectMultiple(id));
      }
    }
    setMarquee(null);
    endGesture();
  };

  const clickSelect = (comp: VisualComponent, e: React.PointerEvent) => {
    e.stopPropagation();
    if (mode === 'move' || mode === 'resize') return;
    if ((comp as any).base?.locked) return;
    if (e.shiftKey) selectMultiple(comp.id);
    else selectComponent(comp.id);
  };

  const handleBackspace = (e: React.KeyboardEvent) => {
    if ((e.key === 'Delete' || e.key === 'Backspace') && selectedComponentId) {
      const t = e.target as HTMLElement;
      if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') return;
      pushHistory();
      useDashboardStore.getState().removeComponent(selectedComponentId);
    }
  };

  // ── 工具条 ──
  const toolbar = (
    <div
      data-testid="ov-toolbar"
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)',
        display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px',
        background: 'rgba(26,26,46,.92)', border: '1px solid #30475e', borderRadius: 8,
        boxShadow: '0 4px 16px rgba(0,0,0,.4)', pointerEvents: 'auto', zIndex: 320,
      }}
    >
      <span data-testid="ov-edit-tag" style={{ color: saveError ? '#ff4d4f' : '#4e9eff', fontSize: 12, marginRight: 4 }}>
        大屏叠加编辑 {saveError ? '✕ 保存失败' : dirty ? '● 未保存' : '· 已保存'}
      </span>
      <span
        data-testid="ov-sel-count"
        style={{ color: selectedComponentIds.length ? '#52c41a' : '#666', fontSize: 12, marginRight: 4 }}
      >已选 {selectedComponentIds.length}</span>
      <ToolButton active={mode === 'select'} title="框选" icon={<AimOutlined />} onClick={() => setMode('select')} />
      <ToolButton active={mode === 'move'} title="移动" icon={<DragOutlined />} onClick={() => setMode('move')} />
      <ToolButton active={mode === 'rotate'} title="旋转" icon={<RetweetOutlined />} onClick={() => setMode('rotate')} />
      <ToolButton active={mode === 'resize'} title="缩放" icon={<ExpandOutlined />} onClick={() => { setResizeHandle('se'); setMode('resize'); }} />
      {mode === 'resize' && (
        <Space size={2} data-testid="ov-resize-handles">
          {(['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as const).map((h) => (
            <Tooltip key={h} title={`方向 ${h}`}>
              <Button
                size="small"
                type={resizeHandle === h ? 'primary' : 'text'}
                style={{ width: 20, padding: 0, fontSize: 10 }}
                onClick={() => setResizeHandle(h)}
                data-testid={`ov-resize-${h}`}
              >{h}</Button>
            </Tooltip>
          ))}
        </Space>
      )}
      <ToolButton active={mode === 'binding'} title="数据绑定" icon={<ApartmentOutlined />} onClick={() => { setMode('binding'); useEditorUiStore.getState().setOverlay2dBinding(true); useEditorUiStore.getState().setOverlayRightTab('props'); }} />
      <div style={{ width: 1, height: 20, background: '#30475e', margin: '0 4px' }} />
      <ToolButton title="属性" icon={<ZoomInOutlined />} onClick={() => { setMode('select'); useEditorUiStore.getState().setOverlay2dBinding(false); useEditorUiStore.getState().setOverlayRightTab('props'); }} />
      <ToolButton title="保存" icon={<SaveOutlined />} onClick={() => void saveSceneAndDash()} />
      <Tooltip title="撤销 Ctrl+Z">
        <Button size="small" type="text" icon={<UndoOutlined />} onClick={undo} data-testid="ov-tool-撤销" />
      </Tooltip>
      <Tooltip title="重做 Ctrl+Y">
        <Button size="small" type="text" icon={<RedoOutlined />} onClick={redo} data-testid="ov-tool-重做" />
      </Tooltip>
      <div style={{ width: 1, height: 20, background: '#30475e', margin: '0 4px' }} />
      <Tooltip title="按住 Alt 拖动可旋转 3D 视角（交还相机）">
        <span style={{ color: altCamera ? '#52c41a' : '#888', fontSize: 11, cursor: 'help' }} data-testid="ov-alt-hint">
          ALT{altCamera ? '·相机' : ''}
        </span>
      </Tooltip>
      <Tooltip title="退出大屏叠加编辑（返回 3D 浏览）">
        <Button size="small" type="text" icon={<CloseOutlined />} data-testid="ov-exit" onClick={() => useEditorUiStore.getState().setOverlayEditMode(false)} />
      </Tooltip>
      {(saving || sceneSaving) && <span style={{ color: '#faad14', fontSize: 11 }}>保存中…</span>}
    </div>
  );

  // ── 选中框 + 手柄 ──
  const selectionBox = selected && selectedComponentIds.length >= 1 && (
    <div
      data-testid="ov-selection"
      style={{
        position: 'absolute',
        left: selected.position.x, top: selected.position.y,
        width: selected.size.width, height: selected.size.height,
        border: '1.5px dashed #4e9eff', boxSizing: 'border-box', pointerEvents: 'none',
      }}
    >
      {(mode === 'move' || mode === 'resize' || mode === 'rotate') && (
        <>
          {[[0, 0], [1, 0], [0, 1], [1, 1]].map(([px, py]) => (
            <span key={`${px}-${py}`} style={{
              position: 'absolute', left: `calc(${px * 100}% - 4px)`, top: `calc(${py * 100}% - 4px)`,
              width: 8, height: 8, background: '#4e9eff', borderRadius: 2,
            }} />
          ))}
          {mode === 'rotate' && (
            <span style={{
              position: 'absolute', left: 'calc(50% - 8px)', top: -26, width: 16, height: 16,
              background: '#2ecc71', borderRadius: '50%', pointerEvents: 'auto', cursor: 'grab',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10,
            }} data-testid="ov-rotate-knob" title="拖动旋转">↻</span>
          )}
        </>
      )}
    </div>
  );

  return (
    <div
      ref={attachShell}
      data-testid="ov-edit-root"
      onPointerDown={onShellPointerDown}
      onPointerMove={onShellPointerMove}
      onPointerUp={onShellPointerUp}
      onPointerCancel={endGesture}
      onKeyDown={handleBackspace}
      tabIndex={0}
      style={{ position: 'absolute', inset: 0, zIndex: 310, pointerEvents: altCamera ? 'none' : 'auto', outline: 'none' }}
    >
      {toolbar}

      {/* G4：2D 组件右键菜单（复制/删除/置顶/置底/锁定/属性/绑定） */}
      {ctxMenu && ctxComp && (
        <>
          <div
            data-testid="ov-ctx-backdrop"
            onPointerDown={() => setCtxMenu(null)}
            onContextMenu={(e) => { e.preventDefault(); setCtxMenu(null); }}
            style={{ position: 'absolute', inset: 0, zIndex: 339, pointerEvents: 'auto' }}
          />
          <div
            data-testid="ov-ctx-menu"
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              position: 'absolute', left: ctxMenu.x, top: ctxMenu.y, zIndex: 340, minWidth: 140,
              background: '#1e1e34', border: '1px solid #30475e', borderRadius: 6,
              boxShadow: '0 8px 24px rgba(0,0,0,.5)', padding: '4px 0', pointerEvents: 'auto',
            }}
          >
            {([
              { key: 'props', label: '属性', testid: 'ov-ctx-props', run: () => { setMode('select'); useEditorUiStore.getState().setOverlay2dBinding(false); } },
              { key: 'binding', label: '数据绑定', testid: 'ov-ctx-binding', run: () => { setMode('binding'); useEditorUiStore.getState().setOverlay2dBinding(true); } },
              { key: 'duplicate', label: '复制', testid: 'ov-ctx-duplicate', run: () => { pushHistory(); useDashboardStore.getState().duplicateComponent?.(ctxComp.id); } },
              { key: 'top', label: '置顶', testid: 'ov-ctx-top', run: () => { pushHistory(); useDashboardStore.getState().moveComponentZ(ctxComp.id, 'top'); } },
              { key: 'bottom', label: '置底', testid: 'ov-ctx-bottom', run: () => { pushHistory(); useDashboardStore.getState().moveComponentZ(ctxComp.id, 'bottom'); } },
              { key: 'lock', label: ctxLocked ? '解锁' : '锁定', testid: 'ov-ctx-lock', run: () => { pushHistory(); useDashboardStore.getState().setComponentLocked(ctxComp.id, !ctxLocked); } },
              { key: 'delete', label: '删除', testid: 'ov-ctx-delete', danger: true, run: () => { pushHistory(); useDashboardStore.getState().removeComponent(ctxComp.id); if (useDashboardStore.getState().selectedComponentId === ctxComp.id) useDashboardStore.getState().selectComponent(null); } },
            ] as { key: string; label: string; testid: string; danger?: boolean; run: () => void }[]).map((it) => (
              <div
                key={it.key}
                data-testid={it.testid}
                onClick={() => { it.run(); setCtxMenu(null); }}
                style={{ padding: '6px 14px', fontSize: 12, cursor: 'pointer', color: it.danger ? '#ff7875' : '#d0d0e0' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(78,158,255,.14)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >{it.label}</div>
            ))}
          </div>
        </>
      )}

      {/* 页面缩放容器（与只读预览一致：分轴拉伸 + 锚点偏移） */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: altCamera ? 'none' : 'auto',
        transform: currentPage?.sceneAnchor
          ? `translate(${currentPage.sceneAnchor.position.x}px, ${currentPage.sceneAnchor.position.y}px) scale(${anchorScale})`
          : undefined,
        transformOrigin: 'center',
      }}>
        <div
          ref={pageRef}
          data-testid="ov-page"
          style={{
            position: 'relative', width: pageW, height: pageH,
            transform: `scale(${scale.x}, ${scale.y})`, transformOrigin: 'top left',
          }}
        >
          {components.map((comp) => {
            const visible = (comp as any).base?.visible ?? (comp as any).config?.visible ?? true;
            if (visible === false) return null;
            return (
              <div key={comp.id} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                <CompPreviewBox comp={comp} />
              </div>
            );
          })}

          {/* 交互热区：与组件同位置同尺寸，透明层承载指针事件 */}
          {mode !== 'binding' && components.map((comp) => {
            const visible = (comp as any).base?.visible ?? (comp as any).config?.visible ?? true;
            if (visible === false) return null;
            const locked = !!(comp as any).base?.locked;
            return (
              <div
                key={`hit-${comp.id}`}
                data-testid={`ov-hit-${comp.id}`}
                onContextMenu={(e) => openCtxMenu(e, comp)}
                onPointerDown={(e) => {
                  if (e.button !== 0) return;
                  if (mode === 'resize' || mode === 'rotate' || mode === 'move') {
                    startGesture(e, mode, comp);
                    return;
                  }
                  // select（默认）：点选 + 按住左键直接拖动（2D 编辑器式；Shift 点选不触发拖动）
                  clickSelect(comp, e);
                  if (!e.shiftKey) startGesture(e, 'move', comp, { selectTap: true });
                }}
                onPointerMove={onPointerMove}
                onPointerUp={endGesture}
                style={{
                  position: 'absolute',
                  left: comp.position.x, top: comp.position.y,
                  width: comp.size.width, height: comp.size.height,
                  zIndex: ((comp as any).base?.zIndex ?? (comp as any).config?.zIndex ?? 1) + 1,
                  pointerEvents: 'auto',
                  cursor: locked ? 'not-allowed' : mode === 'move' ? 'move' : 'pointer',
                  transform: (comp as any).base?.rotation ? `rotate(${(comp as any).base.rotation}deg)` : undefined,
                  background: 'transparent',
                }}
              />
            );
          })}

          {selectionBox}
          {mode === 'rotate' && selected && (
            <div
              onPointerDown={(e) => startGesture(e, 'rotate', selected)}
              onPointerMove={onPointerMove}
              onPointerUp={endGesture}
              data-testid="ov-rotate-surface"
              style={{ position: 'absolute', left: selected.position.x, top: selected.position.y - 40, width: selected.size.width, height: 40, pointerEvents: 'auto', cursor: 'grab' }}
            />
          )}
          {mode === 'resize' && selected && (
            <div
              onPointerDown={(e) => startGesture(e, 'resize', selected)}
              onPointerMove={onPointerMove}
              onPointerUp={endGesture}
              data-testid="ov-resize-surface"
              style={{
                position: 'absolute',
                left: resizeHandle.includes('w') ? selected.position.x - 6 : selected.position.x + selected.size.width - 10,
                top: resizeHandle.includes('n') ? selected.position.y - 6 : selected.position.y + selected.size.height - 10,
                width: 16, height: 16, background: '#4e9eff', border: '1px solid #fff', borderRadius: 2,
                pointerEvents: 'auto', cursor: 'nwse-resize', zIndex: 5,
              }}
            />
          )}

          {marquee && (
            <div
              data-marquee="1"
              style={{
                position: 'absolute',
                left: Math.min(marquee.x1, marquee.x2), top: Math.min(marquee.y1, marquee.y2),
                width: Math.abs(marquee.x2 - marquee.x1), height: Math.abs(marquee.y2 - marquee.y1),
                border: '1px solid #4e9eff', background: 'rgba(78,158,255,.12)', pointerEvents: 'none',
              }}
              data-testid="ov-marquee"
            />
          )}
        </div>
      </div>
    </div>
  );
}
